import React, { useState } from "react";
import { Badge, Button, Group, Loader, Paper, Stack, Table, Text } from "@mantine/core";
import { ProductConfig } from "types/productManagementTypes";
import ConfigJsonView from "./ConfigJsonView";

const TYPE_EXPLANATIONS: Record<string, string> = {
  os: "An OS update the platform queued for this device.",
  app: "An app update the platform queued for this device.",
  config: "A single PlayerPrefs key the platform sent, such as the logger or the API config key.",
  reset: "A reset the platform sent, telling the device to return to defaults.",
  reboot: "A reboot the platform sent to this device.",
  shutdown: "A shutdown the platform sent to this device.",
};

interface OtherConfigEntriesProps {
  entries: ProductConfig[] | null;
  failed?: boolean;
}

const OtherConfigEntries: React.FC<OtherConfigEntriesProps> = ({ entries, failed }) => {
  const [expanded, setExpanded] = useState<string | null>(null);

  const keyFor = (entry: ProductConfig, idx: number) => entry.type ?? `entry-${idx}`;
  const selected = (entries ?? []).find((entry, idx) => keyFor(entry, idx) === expanded);

  return (
    <Paper withBorder radius="sm" p="md">
      <Stack gap="xs">
        <Stack gap={2}>
          <Text fw={600} size="sm">
            Other config entries
          </Text>
          <Text size="xs" c="dimmed">
            Anything else the platform has stored for this device besides the applied config: queued
            OS or app updates, single-key sends, and resets.
          </Text>
        </Stack>

        {failed && (
          <Text size="sm" c="red">
            Failed to load the other config entries.
          </Text>
        )}

        {!failed && entries === null && (
          <Group gap="xs">
            <Loader size="xs" />
            <Text size="sm" c="dimmed">
              Loading entries…
            </Text>
          </Group>
        )}

        {!failed && entries !== null && entries.length === 0 && (
          <Text size="sm" c="dimmed">
            No other entries. Only the applied config is stored for this device.
          </Text>
        )}

        {!failed && entries !== null && entries.length > 0 && (
          <Table verticalSpacing="xs" horizontalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Type</Table.Th>
                <Table.Th>Applied</Table.Th>
                <Table.Th>Date</Table.Th>
                <Table.Th w={90} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {entries.map((entry, idx) => {
                const key = keyFor(entry, idx);
                return (
                  <Table.Tr key={key}>
                    <Table.Td>
                      <Badge variant="light" color="blue" size="sm">
                        {entry.type || "-"}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color={entry.applied ? "green" : "yellow"} size="sm">
                        {entry.applied ? "Yes" : "No"}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {entry.date ? new Date(entry.date).toLocaleString() : "-"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Button
                        size="compact-xs"
                        variant="default"
                        onClick={() => setExpanded((e) => (e === key ? null : key))}
                      >
                        {expanded === key ? "Hide" : "Show"}
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}

        {/* Outside the table: long JSON lines would widen a cell and push the
            table past this card. */}
        {selected && (
          <Stack gap="xs">
            <Text size="xs" c="dimmed">
              {(selected.type && TYPE_EXPLANATIONS[selected.type]) ||
                `The ${selected.type || "untyped"} entry stored for this device.`}
            </Text>
            <ConfigJsonView value={selected.config} />
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default OtherConfigEntries;
